import styled, { css } from 'styled-components'
import type { StyledSvgComponent } from '../../../types/svg'
import { desktopBreakpoint, tabletBreakpoint, wideScreenBreakpoint } from 'react-components'
import FreeCancellation from '../../../assets/icons/free-cancellation-shield.svg'

export const CTAButton = styled.button`
    width: 100%;
    padding: 0.5rem 1.5rem;
    border-radius: 0.75rem;
    font-family: ${props => props.theme.typography.font.family.latoVerdana};
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5rem;
    cursor: pointer;
    text-align: center;
    border: 0.0625rem solid transparent;

    background-color: ${({ theme }) => theme.colors.tfwYellow};
    color: ${({ theme }) => theme.colors.tfwBlack99};

    &:hover {
        background-color: ${({ theme }) => theme.colors.tfwYellowHover};
        box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0.0625rem);
    }
`

export const BreadcrumbContainer = styled.div`
    margin-bottom: 1rem;
`

export const OuterCardWrapper = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        background-color: transparent;
        gap: ${theme.spacing.xSmall};
        padding: 0 0 1.5rem 0;
        margin-bottom: 1.5rem;
        border-bottom: ${theme.typography.border.width.xxxThin} ${theme.typography.border.style.solid}
            ${theme.colors.tfwBlackOpacity10};
        cursor: pointer;
    `,
)

export const CardContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 1rem;
    column-gap: 1.25rem;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) {
        grid-template-columns: 20rem 1fr;
        align-items: stretch;
    }

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        grid-template-columns: 18rem 1fr;
    }

    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        grid-template-columns: 24rem 1fr;
    }
`

export const ImageSection = styled.div(
    () => css`
        border-radius: 1rem;
    `,
)

export const ImageWrapper = styled.div`
    position: relative;
    border-radius: 1rem;
    overflow: hidden;
    aspect-ratio: 3 / 2;
`

export const ContentSection = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        color: ${theme.colors.tfwBlack99};
        padding-left: 0.25rem;
        overflow: hidden;
        position: relative;
    `,
)

export const SelectAnchor = styled.div`
    position: absolute;
    top: 1.75rem;
    right: 0;
    z-index: 2;
`

export const FeaturesWrapper = styled.div`
    margin-top: 0.25rem;
`

export const Title = styled.h2<{ $reserveSelectSpace?: boolean }>(
    ({ theme, $reserveSelectSpace }) => css`
        margin: 0;
        width: 100%;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-size: 1.25rem;
        line-height: 1.75rem;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        ${$reserveSelectSpace &&
        css`
            padding-right: 8.5rem;
        `}
    `,
)

export const StatsRow = styled.div(
    () => css`
        font-size: 0.875rem;
        line-height: 1.375rem;
        color: #282828;
    `,
)

export const RatingWrapper = styled.div`
    display: flex;
    align-items: center;
`

export const RatingRow = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xxSmall};
        font-family: ${theme.typography.font.family.latoVerdana};
        font-size: 0.875rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const RatingText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.latoVerdana};
        font-size: 0.875rem;
        line-height: 1.375rem;
        color: #282828;
    `,
)

export const RatingCount = styled.span(
    () => css`
        font-size: 0.875rem;
        line-height: 1.375rem;
        color: #5f5f5f;
    `,
)

export const DTVSeparator = styled.div`
    width: 0.2rem;
    height: 0.2rem;
    border-radius: 50%;
    background-color: #282828;
    margin: 0 0.5rem;
    align-self: center;
    flex-shrink: 0;
`

export const DTVLabel = styled.span`
    font-family: ${({ theme }) => theme.typography.font.family.lato};
    font-size: 0.75rem;
    line-height: 1.375rem;
    color: #6f6f6f;
    margin-left: 0.25rem;
`

export const Description = styled.p(
    ({ theme }) => css`
        margin-top: ${theme.spacing.xSmall};
        font-family: ${theme.typography.font.family.latoVerdana};
        font-size: 0.875rem;
        color: ${theme.colors.tfwBlack99};
        line-height: 1.4;
    `,
)

export const BookingHighlights = styled.div`
    display: flex;
    margin-top: 0.25rem;
    gap: 0.5rem;
`

export const BottomRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
    margin-top: auto;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) and (max-width: ${tabletBreakpoint.max}px) {
        margin-top: 1rem;
    }
`

export const PriceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
`

export const ButtonWrapper = styled.div`
    min-width: 8rem;
    flex-shrink: 0;
`

export const PriceContainer = styled.div`
    display: flex;
    flex-direction: column-reverse;

    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        flex-direction: row;
    }
`

export const PriceText = styled.div(
    ({ theme }) => css`
        display: flex;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: 600;
        font-size: 1.25rem;
        line-height: 1.75rem;
        color: #282828;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) and (max-width: ${wideScreenBreakpoint.min}px) {
            font-size: 1rem;
            line-height: 1.5rem;
        }
    `,
)

export const SlasherPrice = styled.div(
    ({ theme }) => css`
        display: flex;
        position: relative;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: 500;
        font-size: 1.1rem;
        line-height: 1.75rem;
        color: #aaaaaa;
        margin-right: 8px;
        width: fit-content;
        text-decoration: line-through;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) and (max-width: ${wideScreenBreakpoint.min}px) {
            font-size: 1rem;
            line-height: 1.5rem;
        }
    `,
)

export const PersonDetails = styled.div(
    ({ theme }) => css`
        display: flex;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: 400;
        font-size: 0.75rem;
        line-height: 1rem;
        color: #5f5f5f;
        margin: 0.25rem 0 0;
    `,
)

export const SeoHref = styled.a`
    display: flex;
    font: inherit;
    text-decoration: none;
`

export const ContentHeader = styled.div(
    () => css`
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 0.5rem;
    `,
)

export const RegionTitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    gap: 0.25rem;
`

export const TopTraumTagPill = styled.div<{ $showCheckbox?: boolean }>(
    ({ $showCheckbox }) => css`
        display: flex;
        align-items: center;
        gap: 0.25rem;
        position: absolute;
        top: ${$showCheckbox ? '0.75rem' : '0.5rem'};
        left: ${$showCheckbox ? '3rem' : '0.5rem'};
        border-radius: 1rem;
        background-color: #ffffffcc;
        padding: 0.25rem 0.5rem;
    `,
)

export const VirtualTourWrapper = styled.div`
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
`

export const OfferPriceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`

export const OfferBadge = styled.div(
    () => css`
        display: flex;
        align-items: center;
        width: fit-content;
        min-width: 10.25rem;
        background: linear-gradient(90deg, #d2faea 0%, #ffffff 100%);
        padding: 0.25rem 0.5rem;
        border-radius: 12px;
        border-left: 2px solid var(--colour-border-intense-positive, #0d7c4f);
    `,
)

export const OfferText = styled.span`
    color: #0d7c4f;
    font-size: 0.875rem;
    line-height: 1.375rem;
    font-weight: 400;
`

export const FreeCancellationContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;

    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        margin-left: 0.5rem;
    }
`

export const FreeCancellationIcon = styled(FreeCancellation as StyledSvgComponent)`
    width: 1.25rem;
    height: 1.25rem;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) and (max-width: ${wideScreenBreakpoint.min}px) {
        width: 1rem;
        height: 1rem;
    }
`

export const FreeCancellationText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.375rem;
        color: #282828;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) and (max-width: ${wideScreenBreakpoint.min}px) {
            font-size: 0.75rem;
            line-height: 1rem;
        }
    `,
)

export const BottomTraumTagContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    height: 100%;
    background: linear-gradient(190.38deg, #ffffff 40.45%, #eee3ff 100%);
    padding: 0.5rem;
    border-radius: 0 0 1rem 1rem;
`
